import { ChangeEvent, Children, cloneElement, ReactElement } from "react";
import {
  Tab as MuiTab,
  Tabs as MuiTabs,
  TabsProps as MuiTabsProps,
  TabProps as MuiTabProps,
  withStyles,
  createStyles,
  Theme,
  TabsClassKey,
  TabClassKey,
  Badge,
} from "@material-ui/core";

export interface TabsStyles extends Partial<Record<TabsClassKey, string>> {}

export type TabsProps = {
  onChange: (value: string) => void;
  classes: TabsStyles;
  id: string;
  children: ReactElement<any>[] | ReactElement<any>;
} & Omit<MuiTabsProps, "onChange" | "children">;

const a11yProps = (id: string, index: any) => ({
  id: `${id}-${index}`,
  "aria-controls": `${id}-panel-${index}`,
});

export const Tabs = withStyles((theme: Theme) =>
  createStyles({
    root: {
      borderTopLeftRadius: 4,
      borderTopRightRadius: 4,

      borderBottom: "1px solid rgba(10, 31, 68, 0.05)",

      background: "#fff",
    },
    indicator: {
      display: "flex",
      justifyContent: "center",
      backgroundColor: "transparent",

      "& > span": {
        maxWidth: "auto",
        width: "calc(100% - 32px)",
        backgroundColor: theme.palette.primary.main,
      },
    },
  }),
)(({ classes, onChange, id, children, ...rest }: TabsProps) => {
  const customId = `tabs-${id ?? "noId"}`;

  const handleChange = (_: ChangeEvent<{}>, index: any) => {
    if (onChange) {
      onChange(index);
    }
  };

  return (
    <MuiTabs
      aria-label="tabs"
      {...rest}
      id={customId}
      classes={classes}
      onChange={handleChange}
      TabIndicatorProps={{ children: <span /> }}
    >
      {Children.map(children, (child: ReactElement, index: number) =>
        cloneElement(child, {
          ...a11yProps(customId, index),
        }),
      )}
    </MuiTabs>
  );
});

export interface TabStyles extends Partial<Record<TabClassKey, string>> {}

export type TabProps = {
  id: string;
  classes: TabStyles;
  label: MuiTabProps["label"];
  badge?: boolean;
} & Omit<MuiTabProps, "onChange">;

export const Tab = withStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(2),
      height: 48,
      width: "max-content",
      minWidth: "auto",
    },
    wrapper: {
      fontSize: 12,
      lineHeight: "19px",
      letterSpacing: 1,
    },
  }),
)(({ classes, id, label, badge, ...rest }: TabProps) => (
  <MuiTab
    id={`tab-${id}`}
    {...rest}
    classes={classes}
    label={
      typeof badge !== "undefined" ? (
        <Badge color="secondary" variant="dot">
          {label}
        </Badge>
      ) : (
        label
      )
    }
  />
));
